export const CHAT_AUDIO_EXTS = ['m4a', 'm4b', 'wav', 'mp3'] as const
export const CHAT_AUDIO_MAX_BYTES = 25 * 1024 * 1024

const AUDIO_MIME_ALLOW = new Set([
  'audio/m4a',
  'audio/x-m4a',
  'audio/mp4',
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
])

export type ChatAudioCheck = { ok: true } | { ok: false; reason: string }

export type FiledVoiceMemoDocument = {
  id: string
  title?: string
  filePath?: string
  webappUrl?: string
}

export type IngestedChatAudio = {
  originalName: string
  text: string
  duration?: string
  wordCount?: number
  document: FiledVoiceMemoDocument | null
}

type UploadAudioFn = (files: File[]) => Promise<{
  files?: Array<{ filename: string; originalName?: string }>
  errors?: Array<{ filename: string; error: string }>
}>

type ProcessAudioFn = (
  filename: string,
  source: 'uploaded',
  options?: { folderPath?: string },
) => Promise<{
  transcript?: { text?: string; duration?: string; wordCount?: number }
  document?: FiledVoiceMemoDocument | null
}>

export function extensionOfAudioName(name: string): string {
  const base = name.split(/[/\\]/).pop() ?? name
  const i = base.lastIndexOf('.')
  if (i <= 0 || i === base.length - 1) return ''
  return base.slice(i + 1).toLowerCase()
}

export function isChatAudioFile(file: { name: string; type?: string | null }): boolean {
  const ext = extensionOfAudioName(file.name)
  if ((CHAT_AUDIO_EXTS as readonly string[]).includes(ext)) return true
  if (ext) return false
  const mime = (file.type || '').toLowerCase()
  return AUDIO_MIME_ALLOW.has(mime)
}

export function isChatAudioImportFile(file: {
  name: string
  type?: string | null
  size?: number | null
}): ChatAudioCheck {
  if (!isChatAudioFile(file)) {
    return { ok: false, reason: `${file.name}: not an audio file (.m4a, .m4b, .wav, .mp3)` }
  }
  if (typeof file.size === 'number' && file.size > CHAT_AUDIO_MAX_BYTES) {
    return { ok: false, reason: `${file.name}: over 25MB` }
  }
  return { ok: true }
}

export function workspaceVoiceMemoFolder(contentRoot?: string | null): string {
  const root = (contentRoot ?? '').replace(/^\/+|\/+$/g, '')
  return root ? `${root}/voice-memos` : 'voice-memos'
}

export function partitionComposerDropFiles(files: File[]): {
  textFiles: File[]
  audioFiles: File[]
  rest: File[]
} {
  const textFiles: File[] = []
  const audioFiles: File[] = []
  const rest: File[] = []
  for (const f of files) {
    const n = f.name.toLowerCase()
    if (n.endsWith('.md') || n.endsWith('.txt')) textFiles.push(f)
    else if (isChatAudioFile(f)) audioFiles.push(f)
    else rest.push(f)
  }
  return { textFiles, audioFiles, rest }
}

export function formatVoiceMemoComposerInsert(item: IngestedChatAudio): string {
  const text = (item.text || '').trim()
  if (text) return text
  return `(No speech detected in ${item.originalName})`
}

export async function ingestDroppedAudioFiles(opts: {
  files: File[]
  folderPath: string
  upload: UploadAudioFn
  process: ProcessAudioFn
}): Promise<{
  ingested: IngestedChatAudio[]
  rejected: string[]
  errors: Array<{ name: string; reason: string }>
}> {
  const valid: File[] = []
  const rejected: string[] = []
  for (const f of opts.files) {
    const check = isChatAudioImportFile(f)
    if (check.ok) valid.push(f)
    else rejected.push(check.reason)
  }

  const ingested: IngestedChatAudio[] = []
  const errors: Array<{ name: string; reason: string }> = []

  for (const file of valid) {
    try {
      const uploaded = await opts.upload([file])
      const uploadError = uploaded.errors?.[0]
      if (uploadError) {
        errors.push({ name: file.name, reason: uploadError.error })
        continue
      }
      const filename = uploaded.files?.[0]?.filename
      if (!filename) {
        errors.push({ name: file.name, reason: 'Upload response missing filename' })
        continue
      }
      const processed = await opts.process(filename, 'uploaded', { folderPath: opts.folderPath })
      ingested.push({
        originalName: file.name,
        text: processed.transcript?.text || '',
        duration: processed.transcript?.duration,
        wordCount: processed.transcript?.wordCount,
        document: processed.document ?? null,
      })
    } catch (err) {
      errors.push({
        name: file.name,
        reason: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  return { ingested, rejected, errors }
}
